import { useState, useEffect, useCallback, useMemo } from 'react';
import { get } from '../api';

// "Where applicants come from" — real charts (donut + applicants-vs-hires bars +
// a trend line) with date-range filters. Pure SVG, no chart library.
const C = {
  card: '#1a1208', line: '#3a2a12', cream: '#f5e6c8', gold: '#c9a96e',
  green: '#10b981', dim: '#8a7055', faint: '#5a4030', field: '#2a1f0f',
};
// Distinct, on-brand-ish slice colors (cycled if there are more sources).
const PALETTE = ['#c9a96e', '#10b981', '#f59e0b', '#6366f1', '#e63c3c', '#06b6d4', '#a855f7', '#84cc16'];

function pad(n) { return (n < 10 ? '0' : '') + n; }
function ymd(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return ymd(d); }

const PRESETS = [
  { key: '7', label: 'Last 7 days', from: () => daysAgo(7) },
  { key: '30', label: 'Last 30 days', from: () => daysAgo(30) },
  { key: '90', label: 'Last 90 days', from: () => daysAgo(90) },
  { key: 'all', label: 'All time', from: () => '' },
];

// SVG donut. data = [{label, value, color}]. Renders nothing meaningful at total 0.
function Donut({ data, size = 150 }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const r = size / 2, cx = r, cy = r, stroke = size * 0.18, rad = r - stroke / 2;
  let acc = 0;
  const circ = 2 * Math.PI * rad;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={cx} cy={cy} r={rad} fill="none" stroke={C.field} strokeWidth={stroke} />
      {total > 0 && data.map((d, i) => {
        const frac = d.value / total;
        const dash = `${frac * circ} ${circ}`;
        const seg = (
          <circle key={i} cx={cx} cy={cy} r={rad} fill="none" stroke={d.color} strokeWidth={stroke}
            strokeDasharray={dash} strokeDashoffset={-acc * circ} />
        );
        acc += frac;
        return seg;
      })}
    </svg>
  );
}

// SVG grouped bars: applicants vs hires per source.
function GroupedBars({ rows }) {
  const max = Math.max(1, ...rows.map(r => r.applicants));
  const W = 460, rowH = 34, padL = 130, padR = 56, barH = 11, gap = 3;
  const H = rows.length * rowH + 8;
  const scale = v => (v / max) * (W - padL - padR);
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W }}>
      {rows.map((r, i) => {
        const y = i * rowH + 6;
        return (
          <g key={r.source}>
            <text x={padL - 10} y={y + barH + 1} textAnchor="end" fontSize="12" fill={C.cream} fontFamily="inherit">{r.source}</text>
            <rect x={padL} y={y} width={Math.max(2, scale(r.applicants))} height={barH} rx="2" fill={C.gold} opacity="0.85" />
            <rect x={padL} y={y + barH + gap} width={Math.max(2, scale(r.hires))} height={barH} rx="2" fill={C.green} />
            <text x={padL + Math.max(2, scale(r.applicants)) + 6} y={y + barH} fontSize="10.5" fill={C.dim} fontFamily="inherit">{r.applicants} applied</text>
            <text x={padL + Math.max(2, scale(r.hires)) + 6} y={y + 2 * barH + gap} fontSize="10.5" fill={C.green} fontFamily="inherit">{r.hires} hired · {r.hire_rate}%</text>
          </g>
        );
      })}
    </svg>
  );
}

// SVG trend line/area of applicants over the timeline.
function Trend({ points }) {
  if (!points.length) return null;
  const W = 460, H = 120, padL = 28, padB = 22, padT = 10, padR = 8;
  const max = Math.max(1, ...points.map(p => p.applicants));
  const innerW = W - padL - padR, innerH = H - padB - padT;
  const x = i => padL + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const y = v => padT + innerH - (v / max) * innerH;
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.applicants).toFixed(1)}`).join(' ');
  const area = `${line} L ${x(points.length - 1).toFixed(1)} ${padT + innerH} L ${x(0).toFixed(1)} ${padT + innerH} Z`;
  const ticks = [0, max];
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W }}>
      {ticks.map(t => (
        <g key={t}>
          <line x1={padL} y1={y(t)} x2={W - padR} y2={y(t)} stroke={C.line} strokeWidth="1" />
          <text x={padL - 6} y={y(t) + 3} textAnchor="end" fontSize="10" fill={C.faint} fontFamily="inherit">{t}</text>
        </g>
      ))}
      <path d={area} fill={C.gold} opacity="0.12" />
      <path d={line} fill="none" stroke={C.gold} strokeWidth="2" />
      {points.map((p, i) => <circle key={i} cx={x(i)} cy={y(p.applicants)} r="2.5" fill={C.gold} />)}
      <text x={padL} y={H - 6} fontSize="10" fill={C.faint} fontFamily="inherit">{points[0].day}</text>
      {points.length > 1 && <text x={W - padR} y={H - 6} textAnchor="end" fontSize="10" fill={C.faint} fontFamily="inherit">{points[points.length - 1].day}</text>}
    </svg>
  );
}

export default function ReferralInsights() {
  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const qs = [];
    if (from) qs.push('from=' + from);
    if (to) qs.push('to=' + to);
    try { setData(await get('/careers/referral-metrics' + (qs.length ? '?' + qs.join('&') : ''))); }
    catch { /* ignore */ }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  function applyPreset(p) {
    setPreset(p.key);
    setFrom(p.from());
    setTo(''); // presets are "since N days ago" → today
  }

  // Download the metric as CSV, honouring the active date filter. The shared
  // api() helper is JSON-only, so this goes straight through fetch.
  async function downloadCsv() {
    const qs = [];
    if (from) qs.push('from=' + from);
    if (to) qs.push('to=' + to);
    const token = localStorage.getItem('tnt_token');
    try {
      const res = await fetch((process.env.REACT_APP_API_URL || '') + '/api/careers/referral-metrics.csv' + (qs.length ? '?' + qs.join('&') : ''), { headers: token ? { Authorization: 'Bearer ' + token } : {} });
      if (!res.ok) return;
      const m = (res.headers.get('Content-Disposition') || '').match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url; a.download = (m && m[1]) || 'referral-sources.csv';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  }

  const colored = useMemo(() => {
    if (!data || !data.sources) return [];
    return data.sources.map((s, i) => ({ ...s, color: PALETTE[i % PALETTE.length] }));
  }, [data]);

  const hasData = data && data.sources && data.sources.length > 0;
  const lbl = { color: C.dim, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, display: 'block' };
  const dateInput = { background: C.field, border: '1px solid ' + C.line, borderRadius: 6, padding: '6px 8px', color: C.cream, fontSize: 12, fontFamily: 'inherit', colorScheme: 'dark' };

  return (
    <div style={{ background: C.card, border: '1px solid ' + C.line, borderRadius: 10, padding: '16px 18px', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: C.gold, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>📊 Where applicants come from</span>
          {data && <span style={{ color: C.faint, fontSize: 11 }}>· {data.totals.applicants} tracked{data.unknown ? ` · ${data.unknown} unknown` : ''}</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {PRESETS.map(p => (
            <button key={p.key} onClick={() => applyPreset(p)} style={{ background: preset === p.key ? C.gold : 'transparent', color: preset === p.key ? '#1a1208' : C.dim, border: '1px solid ' + (preset === p.key ? C.gold : C.line), borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{p.label}</button>
          ))}
          {hasData && <button onClick={downloadCsv} title="Export this view as CSV" style={{ background: 'transparent', color: C.gold, border: '1px solid ' + C.gold + '66', borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>⬇ CSV</button>}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div><label style={lbl}>From</label><input type="date" value={from} max={to || undefined} onChange={e => { setFrom(e.target.value); setPreset('custom'); }} style={dateInput} /></div>
        <div><label style={lbl}>To</label><input type="date" value={to} min={from || undefined} onChange={e => { setTo(e.target.value); setPreset('custom'); }} style={dateInput} /></div>
        {(from || to) && <button onClick={() => { setFrom(''); setTo(''); setPreset('all'); }} style={{ background: 'transparent', color: C.dim, border: '1px solid ' + C.line, borderRadius: 6, padding: '6px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>Clear</button>}
      </div>

      {!hasData ? (
        <p style={{ color: C.faint, fontSize: 13, padding: '8px 0' }}>No referral data in this range yet. Applicants pick "How did you hear about us?" on the application form — those answers show up here.</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28 }}>
          {/* Donut + legend */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <Donut data={colored.map(s => ({ label: s.source, value: s.applicants, color: s.color }))} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {colored.map(s => {
                const pct = data.totals.applicants ? Math.round((s.applicants / data.totals.applicants) * 100) : 0;
                return (
                  <div key={s.source} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <span style={{ width: 11, height: 11, borderRadius: 3, background: s.color, flexShrink: 0 }} />
                    <span style={{ color: C.cream }}>{s.source}</span>
                    <span style={{ color: C.dim }}>{pct}% · {s.applicants}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Applicants vs hires */}
          <div style={{ flex: '1 1 320px', minWidth: 300 }}>
            <div style={{ color: C.dim, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Applicants vs. hires by source</div>
            <GroupedBars rows={colored} />
          </div>

          {/* Trend over time */}
          {data.timeline && data.timeline.length > 0 && (
            <div style={{ flex: '1 1 100%' }}>
              <div style={{ color: C.dim, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Applicants over time</div>
              <Trend points={data.timeline} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
